import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { WorkflowsService } from '../../services/workflows.service';
import * as workflowsActions from '../../store/workflows.actions';
import {
  selectWorkflowRules,
  selectWorkflowsLoadError,
  selectWorkflowsLoading,
} from '../../store/workflows.selectors';
import {
  IWorkflowExecution,
  IWorkflowRuleSummary,
  WorkflowHistoryStatusFilter,
  WorkflowExecutionStatus,
  WorkflowTriggerType,
} from '../../models/workflows.models';
import {
  executionTargetLabel,
  executionTargetUrl,
} from '../../utils/workflow-execution-link.util';

export type WorkflowsMainTab = 'rules' | 'history';

const TRIGGER_CATEGORIES: Record<WorkflowTriggerType, 'inbox' | 'order'> = {
  new_email_received: 'inbox',
  email_categorized: 'inbox',
  task_status_changed: 'inbox',
  thread_assigned: 'inbox',
  order_created_mto: 'order',
  order_created_smart_inbox: 'order',
  user_mentioned_note: 'inbox',
};

const CATEGORY_ICONS: Record<'inbox' | 'order', string> = {
  inbox: 'inbox',
  order: 'quotations',
};

const HISTORY_STATUS_LABEL_KEYS: Record<WorkflowExecutionStatus, string> = {
  success: 'workflows.history.status.success',
  partial_failure: 'workflows.history.status.partial_failure',
  failure: 'workflows.history.status.failure',
  skipped: 'workflows.history.status.skipped',
  chain_limit: 'workflows.history.status.chain_limit',
};

const HISTORY_STATUS_CLASS_NAMES: Record<WorkflowExecutionStatus, string> = {
  success: 'workflows-history-table__status--success',
  partial_failure: 'workflows-history-table__status--partial-failure',
  failure: 'workflows-history-table__status--failure',
  skipped: 'workflows-history-table__status--skipped',
  chain_limit: 'workflows-history-table__status--chain-limit',
};

const HISTORY_STATUS_ORDER: WorkflowExecutionStatus[] = [
  'success',
  'partial_failure',
  'failure',
  'skipped',
  'chain_limit',
];

@Component({
  selector: 'app-workflows-page',
  templateUrl: './workflows-page.component.html',
  styleUrls: ['./workflows-page.component.scss'],
})
export class WorkflowsPageComponent implements OnInit, OnDestroy {
  readonly loading$: Observable<boolean> = this.store.select(selectWorkflowsLoading);
  readonly loadError$: Observable<boolean> = this.store.select(selectWorkflowsLoadError);
  readonly canEdit$ = this.store.select(selectCanDo('workflow', 'edit'));
  readonly canDelete$ = this.store.select(selectCanDo('workflow', 'delete'));
  readonly canEditOrDelete$ = combineLatest([this.canEdit$, this.canDelete$]).pipe(
    map(([canEdit, canDelete]) => canEdit || canDelete)
  );

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  readonly historySearchControl = new FormControl<string>('', { nonNullable: true });
  readonly historyStatusControl = new FormControl<WorkflowHistoryStatusFilter>('', {
    nonNullable: true,
  });

  readonly listVm$: Observable<{
    active: IWorkflowRuleSummary[];
    inactive: IWorkflowRuleSummary[];
  }> = combineLatest([
    this.store.select(selectWorkflowRules),
    this.searchControl.valueChanges.pipe(debounceTime(200), startWith('')),
  ]).pipe(
    map(([rules, rawQuery]) => {
      const q = (rawQuery ?? '').trim().toLowerCase();
      const filtered = q
        ? rules.filter((r) => r.name.toLowerCase().includes(q))
        : rules;
      return {
        active: filtered.filter((r) => r.status === 'active'),
        inactive: filtered.filter((r) => r.status !== 'active'),
      };
    })
  );

  mainTab: WorkflowsMainTab = 'rules';
  activeSectionOpen = true;
  inactiveSectionOpen = false;
  ruleMenuContext: IWorkflowRuleSummary | null = null;
  historyExecutions: IWorkflowExecution[] = [];
  historyLoading = false;
  historyLoadingMore = false;
  historyLoadError = false;

  private readonly destroy$ = new Subject<void>();
  private historyLoaded = false;
  private historyPage = 1;
  private readonly historyLimit = 25;
  private historyTotalPages = 1;
  /** Monotonic counter used to discard responses from stale history requests (e.g. when filters change while a request is in-flight). */
  private historyRequestKey = 0;
  private historySearchQuery = '';
  private historyStatusFilter: WorkflowHistoryStatusFilter = '';

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private translate: TranslateService,
    private workflowsService: WorkflowsService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(workflowsActions.GetWorkflowRules());
    this.bindHistoryFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setMainTab(tab: WorkflowsMainTab): void {
    this.mainTab = tab;
    if (tab === 'history') {
      this.loadHistory(true);
    }
  }

  createRule(): void {
    this.router.navigate(['/workflows/new-rule']);
  }

  editRule(rule: IWorkflowRuleSummary): void {
    this.router.navigate(['/workflows/edit-rule', rule.id]);
  }

  deleteRule(rule: IWorkflowRuleSummary): void {
    this.store.dispatch(workflowsActions.DeleteWorkflowRule({ id: rule.id }));
  }

  categoryIcon(trigger: WorkflowTriggerType): string {
    const category = TRIGGER_CATEGORIES[trigger] ?? 'inbox';
    return CATEGORY_ICONS[category] ?? 'inbox';
  }

  triggerLabelKey(trigger: WorkflowTriggerType): string {
    return `workflows.triggers.${trigger}`;
  }

  isRuleUiEnabled(rule: IWorkflowRuleSummary): boolean {
    return rule.status === 'active';
  }

  onRuleToggle(rule: IWorkflowRuleSummary, enabled: boolean): void {
    const newStatus = enabled ? 'active' : 'paused';
    this.store.dispatch(
      workflowsActions.ToggleWorkflowRuleStatus({ id: rule.id, status: newStatus })
    );
  }

  trackByRuleId(_index: number, rule: IWorkflowRuleSummary): string {
    return rule.id;
  }

  trackByExecutionId(_index: number, execution: IWorkflowExecution): string {
    return execution.id;
  }

  get historyStatusOptions(): SelectOption[] {
    return [
      {
        value: '',
        label: this.translate.instant('workflows.history.filters.status_all'),
      },
      ...HISTORY_STATUS_ORDER.map((status) => ({
        value: status,
        label: this.translate.instant(HISTORY_STATUS_LABEL_KEYS[status]),
      })),
    ];
  }

  hasActiveHistoryFilters(): boolean {
    return Boolean(this.historySearchQuery || this.historyStatusFilter);
  }

  statusLabelKey(status: IWorkflowExecution['status']): string {
    return HISTORY_STATUS_LABEL_KEYS[status];
  }

  historyStatusClass(status: IWorkflowExecution['status']): string {
    return HISTORY_STATUS_CLASS_NAMES[status];
  }

  historyActionsCount(execution: IWorkflowExecution): number {
    return execution.actionsExecuted + execution.actionsFailed;
  }

  /** Redirect target + label for a history row (order or thread); used by the template. */
  readonly historyTargetUrl = executionTargetUrl;
  readonly historyTargetLabel = executionTargetLabel;

  onHistoryScroll(event: Event): void {
    if (this.historyLoading || this.historyLoadingMore) {
      return;
    }

    const container = event.target as HTMLElement | null;
    if (!container) {
      return;
    }

    const thresholdPx = 80;
    const isNearBottom =
      container.scrollTop + container.clientHeight >= container.scrollHeight - thresholdPx;

    if (isNearBottom && this.historyPage < this.historyTotalPages) {
      this.loadHistoryPage(this.historyPage + 1, true);
    }
  }

  private loadHistory(force = false): void {
    if (!force && (this.historyLoading || this.historyLoadingMore || this.historyLoaded)) {
      return;
    }

    if (force) {
      this.resetHistoryState();
    }

    this.loadHistoryPage(1, false);
  }

  /**
   * Fetch a single page of history from the backend.
   *
   * A fresh load (append=false) bumps `historyRequestKey` so any in-flight
   * request from a previous filter/search is treated as stale and its
   * response is silently ignored. Appending (infinite scroll) keeps the
   * same key because it belongs to the current filter session.
   */
  private loadHistoryPage(page: number, append: boolean): void {
    const requestKey = append ? this.historyRequestKey : this.historyRequestKey + 1;

    if (!append) {
      this.historyRequestKey = requestKey;
    }

    if (append) {
      this.historyLoadingMore = true;
    } else {
      this.historyLoading = true;
      this.historyLoadError = false;
    }

    this.workflowsService
      .getGlobalHistory(page, this.historyLimit, this.historySearchQuery, this.historyStatusFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (requestKey !== this.historyRequestKey) {
            return;
          }

          const rows = response.data ?? [];
          this.historyExecutions = append
            ? [...this.historyExecutions, ...rows]
            : rows;
          this.historyPage = response.metadata?.page ?? page;
          this.historyTotalPages = response.metadata?.pages ?? 1;
          this.historyLoaded = true;
          this.historyLoading = false;
          this.historyLoadingMore = false;
        },
        error: () => {
          if (requestKey !== this.historyRequestKey) {
            return;
          }

          this.historyLoadError = true;
          this.historyLoading = false;
          this.historyLoadingMore = false;
        },
      });
  }

  /** Subscribe to the history search/status filter controls and reload when they change. */
  private bindHistoryFilters(): void {
    combineLatest([
      this.historySearchControl.valueChanges.pipe(
        startWith(this.historySearchControl.value),
        debounceTime(200),
        map((value) => value.trim()),
        distinctUntilChanged()
      ),
      this.historyStatusControl.valueChanges.pipe(
        startWith(this.historyStatusControl.value),
        distinctUntilChanged()
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([searchQuery, statusFilter]) => {
        this.historySearchQuery = searchQuery;
        this.historyStatusFilter = statusFilter;

        if (this.mainTab === 'history') {
          this.loadHistory(true);
        } else {
          this.resetHistoryState();
        }
      });
  }

  /** Reset all history pagination and loading flags so the next load starts fresh. */
  private resetHistoryState(): void {
    this.historyExecutions = [];
    this.historyPage = 1;
    this.historyTotalPages = 1;
    this.historyLoaded = false;
    this.historyLoading = false;
    this.historyLoadingMore = false;
    this.historyLoadError = false;
  }
}
